import * as React from "react";
import { useLayoutEffect, useState } from "react";
import apiClient from "@/api-client";
import { Box, Button, Card, Divider, Link, Stack, Tab, Tabs } from "@mui/material";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { logger } from "@/lib/default-logger";
import { agentsKeys } from "@/hooks/api/use-agents";
import { callLogsKeys } from "@/hooks/api/use-call-logs";
import { campaignsKeys } from "@/hooks/api/use-campaigns";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { RouterLink } from "@/components/core/link";
import { toast } from "@/components/core/toaster";
import { CustomSkeleton } from "@/components/customSkeleton/custom-skeleton";

import { Flow } from "../types";
import { AdvancedSettings } from "./advanced-settings";
import { BasicInformation } from "./basic-information";
import { Service } from "./service";
import { ToolEditor } from "@/components/tools/tool-editor";

function formatServiceName(service: string) {
	switch (service) {
		case "tts": {
			return "TTS";
		}
		case "llm": {
			return "LLM";
		}
		case "stt": {
			return "STT";
		}
		case "voice": {
			return "Voice";
		}
		case "webhook": {
			return "Webhook";
		}
		case "websocket": {
			return "Websocket";
		}
		case "embedding": {
			return "Embedding";
		}
		case "ai-sdk": {
			return "AI SDK";
		}
		case "realtime": {
			return "Realtime";
		}
		case "oai-realtime": {
			return "Realtime";
		}
		default: {
			return service;
		}
	}
}

enum AgentTab {
  TOOLS = "tools",
	BASIC_INFORMATION = "basic-information",
	ADVANCED_SETTINGS = "advanced-settings",
	tts = "tts",
	llm = "llm",
	stt = "stt",
	voice = "voice",
	webhook = "webhook",
	websocket = "websocket",
	embedding = "embedding",
}

const defaultTabs = [{ label: "Main Settings", value: AgentTab.BASIC_INFORMATION }];

export function CreateAgent(): React.JSX.Element {
	const { agentId } = useParams();
	const queryClient = useQueryClient();

	const [isAgentLoading, setIsAgentLoading] = useState(agentId ? true : false);
	const [agent, setAgent] = useState<any | null>(null);

	const [tabs, setTabs] = useState<{ label: string; value: AgentTab }[]>(defaultTabs);
	const [activeTab, setActiveTab] = useState(AgentTab.BASIC_INFORMATION);

	const [flows, setFlows] = useState<Flow[]>([]);
  const [flowSettings, setFlowSettings] = useState<any[]>([]);
	const [flowSettingsAdvanced, setFlowSettingsAdvanced] = useState<any[]>([]);
	const [isFlowsLoading, setIsFlowsLoading] = useState(true);

	const [servicesPlugins, setServicesPlugins] = useState<{ [key: string]: any }>({});
	const [isServicesPluginsLoading, setIsServicesPluginsLoading] = useState(true);

	const [serviceSettings, setServiceSettings] = useState<{ [key: string]: any }>({});
	const [isServiceSettingsLoading, setIsServiceSettingsLoading] = useState(true);

	const navigate = useNavigate();

	const isEdit = agentId;

	const methods = useForm<any>({
		defaultValues: {
			name: "",
			prompt: "",
      hasInboundPrompt: false,
      inboundPrompt: "",
		},
	});

	const {
		handleSubmit,
		setError,
		reset,
		control,
		setValue,
		unregister,
		trigger,
		clearErrors,
    getValues,
		formState: { isSubmitting },
	} = methods;

	const values = useWatch({ control });

	const fetchFlows = async () => {
    setIsFlowsLoading(true);
    
		try {
      const response = await apiClient.get(`/conversation-flows`);
			setFlows(response.data);
      
      const flowName = getValues()?.flow || response.data[0].name;

			if (!isEdit) {
				setValue("flow", flowName);
			}

			if (response.data.length > 0) {
				await handleFlowChange(flowName, response.data);
			}
		} catch (error) {
			logger.error(error);
		} finally {
			setIsFlowsLoading(false);
		}
	};

	const fetchAgent = async () => {
		setIsAgentLoading(true);

		try {
			const response = await apiClient.get(`/agents/${agentId}`);

			setAgent(response.data);

      const agentValues = {
				name: response.data.name,
				prompt: response.data.prompt,
				flow: response.data.flow.name,
				settings: response.data.settings,
        inboundPrompt: response.data.inboundPrompt,
        hasInboundPrompt: response.data?.inboundPrompt?.length > 0,
				...Object.fromEntries(
					response.data.services.map((service: any) => [
						service.type,
						{
							pluginId: service.plugin.id,
							settings: service.settings,
						},
					])
				),
			}

      reset(agentValues);
      await fetchFlows();
      reset(agentValues);
		} catch (error) {
			logger.error(error);
		} finally {
			setIsAgentLoading(false);
		}
	};

	useLayoutEffect(() => {
		if (agentId) {
			fetchAgent();
		} else {
			fetchFlows();
		}
	}, [agentId]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: AgentTab) => {
		setActiveTab(newValue);
		clearErrors();
	};

	const fetchServicePlugins = async (service: string) => {
		setIsServicesPluginsLoading(true);

    const values = getValues();
    
		try {
      const response = await apiClient.get(`/plugin-services/${service}`);
			setServicesPlugins((prev) => ({ ...prev, [service]: response.data }));
      
      const pluginId = values?.[service]?.pluginId || response.data?.[0]?.id;

			if (response.data.length > 0) {
				await fetchServiceSettings(service, pluginId);
			} else {
				setIsServiceSettingsLoading(false);
			}

			if (!isEdit) {
				setValue(`${service}.pluginId`, pluginId);
			}
		} catch (error) {
			toast.error("Something went wrong!");
			logger.error(error);
		} finally {
			setIsServicesPluginsLoading(false);
		}
	};

	const handleFlowChange = async (value: string, defaultFlows = flows) => {
		if (values.settings) {
			for (const key of Object.keys(values.settings)) {
				unregister(`settings.${key}`);
			}
		}
		const flow = defaultFlows.find((flow: any) => flow.name === value);
    
    const mainFields: any[] = [];
    const advancedFields: any[] = [];

    for (const field of flow?.fields || []) {
      if (field.tab === 'main') {
        mainFields.push(field);
      } else {
        advancedFields.push(field);
      }
    }
		
    setFlowSettings(mainFields);
		setFlowSettingsAdvanced(advancedFields);
		
    setValue("settings", Object.fromEntries(flow?.fields?.map((field: any) => [field.name, field.value]) || []));
		const flowServices = flow?.services || [];

		if (flowServices.length === 0) {
			setServicesPlugins({});
			setFlowSettings([]);
      setFlowSettingsAdvanced([]);
			setTabs([...defaultTabs]);
			return;
		}

		try {
			const promises = flowServices.map((service: string) => fetchServicePlugins(service));
			setTabs([
				...defaultTabs,
				...flowServices.map((service: string) => ({ label: formatServiceName(service), value: service as AgentTab })),
				{ label: "Advanced", value: AgentTab.ADVANCED_SETTINGS },
			]);
			await Promise.all(promises);
		} catch (error) {
			logger.error(error);
			toast.error("Something went wrong!");
		}
	};

	const fetchServiceSettings = async (service: string, plugin: string) => {
		if (!plugin) {
			setServiceSettings((prev) => ({ ...prev, [service]: null }));
			for (const key of Object.keys(values[service]?.settings)) {
				unregister(`${service}.settings.${key}`);
			}
			return;
		}

		setIsServiceSettingsLoading(true);

		try {
			const response = await apiClient.get(`/plugin-services/${service}/${plugin}`);
			setServiceSettings((prev) => ({ ...prev, [service]: response.data }));

			setValue(
				`${service}.settings`,
				Object.fromEntries(response.data.map((setting: any) => [setting.name, setting.value]))
			);
		} catch (error) {
			logger.error(error);
			toast.error("Something went wrong!");
		} finally {
			setIsServiceSettingsLoading(false);
		}
	};

	const onSubmitForm = async (values: any) => {
		if (!values.flow || values.name === "" || values.prompt === "" || (values.hasInboundPrompt && !values.inboundPrompt)) {
			if (!values.flow) setError("flow", { message: "Flow is required" });
			if (values.name === "") setError("name", { message: "Name is required" });
			if (values.prompt === "") setError("prompt", { message: "Prompt is required" });
			if (values.hasInboundPrompt && !values.inboundPrompt) setError("inboundPrompt", { message: "Inbound prompt is required" });
			setActiveTab(AgentTab.BASIC_INFORMATION);
			return;
		}

		if (flowSettings.length > 0) {
			const requiredFieldsWithoutValues = flowSettings.filter((field: any) => {
				if (!field.required || !(field.name in values.settings)) return false;

				const fieldValue = values.settings?.[field.name];
				return fieldValue === undefined || fieldValue === null || fieldValue === "";
			});

			if (requiredFieldsWithoutValues.length > 0) {
				for (const field of flowSettings) {
					if (field.required) {
						setTimeout(() => {
							trigger(`settings.${field.name}`);
						}, 200);
					}
				}

				setActiveTab(AgentTab.BASIC_INFORMATION);
				return;
			}
		}

    if (flowSettingsAdvanced.length > 0) {
			const requiredFieldsWithoutValues = flowSettingsAdvanced.filter((field: any) => {
				if (!field.required || !(field.name in values.settings)) return false;

				const fieldValue = values.settings?.[field.name];
				return fieldValue === undefined || fieldValue === null || fieldValue === "";
			});

			if (requiredFieldsWithoutValues.length > 0) {
				for (const field of flowSettingsAdvanced) {
					if (field.required) {
						setTimeout(() => {
							trigger(`settings.${field.name}`);
						}, 200);
					}
				}

				setActiveTab(AgentTab.ADVANCED_SETTINGS);
				return;
			}
		}

		try {
			await (isEdit
				? apiClient.put(`/agents/${agentId}`, {
						name: values.name,
						prompt: values.prompt,
            inboundPrompt: values.hasInboundPrompt ? values.inboundPrompt : null,
						services: flows
							.find((flow) => flow.name === values.flow)
							?.services.map((service) => ({
								type: service,
								pluginId: values?.[service]?.pluginId,
								settings: values?.[service]?.settings,
							})),
						settings: values.settings,
					})
				: apiClient.post("/agents", {
						flow: values.flow,
						name: values.name,
						prompt: values.prompt,
            inboundPrompt: values.hasInboundPrompt ? values.inboundPrompt : null,
						services: flows
							.find((flow) => flow.name === values.flow)
							?.services.map((service) => ({
								type: service,
								pluginId: values?.[service]?.pluginId,
								settings: values?.[service]?.settings,
							})),
						settings: values.settings,
					}));

			toast.success(isEdit ? "Agent updated" : "Agent created");

			queryClient.invalidateQueries({ queryKey: agentsKeys.lists() });
			
      if (isEdit) {
				queryClient.removeQueries({ queryKey: callLogsKeys.all });
				queryClient.removeQueries({ queryKey: campaignsKeys.all });
			}

			navigate("/agents");
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				toast.error(errorMessages);
				return;
			}
			const errorMessage = errorMessages[0];

			const key = errorMessage.split(" ")[0];
			const tabIndex = Number(key.split(".")[1]);

			if (["settings"].includes(key)) {
				setActiveTab(AgentTab.ADVANCED_SETTINGS);
				setError(key, { message: errorMessage });
				return;
			} else {
				setActiveTab((tabs[tabIndex + 1]?.value as AgentTab) || AgentTab.BASIC_INFORMATION);
				setTimeout(() => {
					trigger();
				}, 200);
			}

			toast.error(
				errorMessage.split(" ")[0].split(".").pop() + " " + errorMessage?.split(" ").slice(1).join(" ") ||
					"Something went wrong!"
			);
		}
	};

	const isLoading = isAgentLoading || isFlowsLoading || isServicesPluginsLoading || isServiceSettingsLoading;
  const hasInboundPrompt = values.hasInboundPrompt;

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/agents"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Agents
							</Link>
							{isEdit && (
								<>
									<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
										/
									</Typography>
									{isLoading ? (
										<CustomSkeleton width={100} height={20} />
									) : (
										<Link
											color="text.primary"
											component={RouterLink}
											href={`/agents`}
											sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
											variant="subtitle2"
										>
											{agent?.name}
										</Link>
									)}
								</>
							)}
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								{isEdit ? "Edit" : "Create"}
							</Typography>
						</Breadcrumbs>

						<Typography variant="h4">{isEdit ? "Agent Edit" : "Agent Create"}</Typography>
					</Stack>

					<Card sx={{ position: "relative" }}>
						<ComponentLoader loading={isLoading} />
						<Tabs onChange={handleTabChange} sx={{ px: 3 }} value={activeTab} variant="scrollable">
							{[...tabs, ...(agentId ? [{ label: "Tools", value: AgentTab.TOOLS }] : [])].map((tab) => (
								<Tab
									iconPosition="end"
									key={tab.value}
									label={tab.label}
									sx={{ minHeight: "auto" }}
									tabIndex={0}
									value={tab.value}
								/>
							))}
						</Tabs>

						<Divider />

						<Box sx={{ p: 2, py: 2.5 }}>
							<FormProvider {...methods}>
                {activeTab === AgentTab.TOOLS && agentId && <ToolEditor scope="agents" id={agentId} />}
								{activeTab === AgentTab.BASIC_INFORMATION && (
									<BasicInformation control={control} flows={flows} onFlowChange={handleFlowChange} isEdit={isEdit} settings={flowSettings} hasInboundPrompt={hasInboundPrompt} />
								)}
								{activeTab === AgentTab.ADVANCED_SETTINGS && (
									<AdvancedSettings control={control} settings={flowSettingsAdvanced} />
								)}
								{activeTab !== AgentTab.TOOLS && activeTab !== AgentTab.BASIC_INFORMATION && activeTab !== AgentTab.ADVANCED_SETTINGS && (
									<Service
										control={control}
										service={activeTab}
										plugins={servicesPlugins?.[activeTab]}
										onPluginChange={fetchServiceSettings}
										settings={serviceSettings?.[activeTab]}
									/>
								)}

								{activeTab !== AgentTab.TOOLS && <Button
									onClick={handleSubmit(onSubmitForm)}
									variant="contained"
									disabled={isSubmitting}
									sx={{ mt: 2.5 }}
								>
									{isEdit ? "Update Agent" : "Create Agent"}
								</Button>}
							</FormProvider>
						</Box>
					</Card>
				</Stack>
			</Box>
		</React.Fragment>
	);
}
